import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'

import { getMessaging, getToken, onMessage } from 'firebase/messaging'

import { useAppDispatch, useAppSelector } from '../store'
import { slice as authSlice } from '../store/auth'
import { slice as outletSlice } from '../store/outlet'
import { authApi } from '../store/api/authEndpoints'
import { outletApi } from '../store/api/outletEndpoints'

const LOGIN_ROUTE = '/login'

interface Props {
  children: React.ReactNode
}

const RefreshToken: React.FC<Props> = ({ children }) => {
  // --- EXTERNAL STATE --- //

  const location = useLocation()

  const dispatch = useAppDispatch()
  const auth = useAppSelector((state) => state.auth)

  // --- RTK QUERY HOOKS --- //

  // When status is 'checking' AND token exists, fetch user data
  const shouldSkipGetMe = auth.status !== 'checking' || !auth.accessToken
  const { data: user } = authApi.useGetMeQuery(undefined, { skip: shouldSkipGetMe })

  // When logged in, load outlet data
  const shouldSkipOutlet = auth.status !== 'loggedIn'
  const { data: outlet } = outletApi.useGetOutletQuery(undefined, { skip: shouldSkipOutlet })

  // Push token mutation
  const [registerPushToken] = authApi.useRegisterPushTokenMutation()

  // --- CONSTANTS --- //

  const isLoginRoute = location.pathname === LOGIN_ROUTE

  // --- EFFECTS --- //

  // Bootstrap: when status is 'checking' and token exists, dispatch loginSuccess
  // This ensures the token is in Redux state before getMe fires
  React.useEffect(() => {
    if (auth.status === 'checking' && auth.accessToken) {
      dispatch(authSlice.actions.loginSuccess(auth.accessToken))
    }
  }, [auth.status, auth.accessToken, dispatch])

  // When getMe returns user data, update auth state
  React.useEffect(() => {
    if (user && auth.status === 'checking') {
      dispatch(authSlice.actions.userLoadSuccess(user))
    }
  }, [user, auth.status, dispatch])

  // When outlet loads, sync to Redux store (for components that read state.outlet)
  React.useEffect(() => {
    if (outlet) {
      dispatch(outletSlice.actions.loadSuccess(outlet))
    }
  }, [outlet, dispatch])

  // Register push token when user is loaded
  React.useEffect(() => {
    if (auth.user && 'serviceWorker' in navigator) {
      const user = auth.user
      const messaging = getMessaging()

      navigator.serviceWorker
        .getRegistration()
        .then((reg) => getToken(messaging, { serviceWorkerRegistration: reg }))
        .then((token) => {
          if (token) {
            dispatch(authSlice.actions.saveTokenStart({ userId: user.code, token }))
            const platform = navigator.userAgent.includes('Android')
              ? 'Android'
              : navigator.userAgent.includes('iPhone') || navigator.userAgent.includes('iPad')
                ? 'Ios'
                : 'Desktop'
            registerPushToken({ token, platform })
            onMessage(messaging, (payload) => {
              // Push notification received
            })
          }
        })
        .catch(() => {
          // Push token registration failed silently
        })
    }
  }, [auth.user, dispatch, registerPushToken])

  // --- RENDERING --- //

  return auth.status === 'checking' ? (
    <div>
      <p>...</p>
    </div>
  ) : isLoginRoute && auth.status === 'loggedIn' ? (
    <Navigate to="/orders" replace />
  ) : !isLoginRoute && auth.status === 'loggedOut' ? (
    <Navigate to={LOGIN_ROUTE} replace />
  ) : (
    <>{children}</>
  )
}

export default RefreshToken
